<template>
  <div class="verification-details">
    <template v-if="isLoaded">
      <template v-if="isFailed">
        <error-message :message="errorMessage | globalize" />
      </template>
      <template v-else>
        <div class="verification-details__request app__block">
          <h2 class="verification-details__title">
            {{ 'verification-details.title-details' | globalize }}
          </h2>
          <ul class="app-key-value__list">
            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'verification-details.lbl-id' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="verification.id">
                {{ verification.id || '-' }}
              </span>
            </li>

            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'verification-details.lbl-remember-me-id' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="verification.rememberMeId">
                {{ verification.rememberMeId || '-' }}
              </span>
            </li>

            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'verification-details.lbl-verify-status' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="verification.status">
                {{ verification.status | formatVerificationStatus }}
              </span>
            </li>

            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'verification-details.lbl-type' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="verification.type">
                {{ verification.type | formatVerificationType }}
              </span>
            </li>
          </ul>

          <div class="verification-details__actions-wrp">
            <permission-wrapper
              skip-alert
              page-name="user"
              resource-name="user"
              action-name="read_foreign">
              <button
                v-ripple
                class="app__form-submit-btn app__button-raised"
                :disabled="!user.id"
                @click="redirectToUser(user.id)"
              >
                {{ 'verification-details.btn-user' | globalize }}
              </button>
            </permission-wrapper>
          </div>

          <div class="verification-details__actions app__form-actions">
            <template
              v-if="
                verification.status === VERIFICATION_PENDING">
              <div class="verification-details__actions-wrp">
                <permission-wrapper
                  skip-alert
                  page-name="quickbuy"
                  resource-name="quickbuy_voucher"
                  action-name="update_foreign">
                  <button
                    v-ripple
                    class="app__form-submit-btn app__button-raised"
                    @click="isFormValid() && submit(true)"
                    :disabled="isDisabled && formMixin.isDisabled"
                  >
                    {{ 'verification-details.btn-approve' | globalize }}
                  </button>
                  <template
                    v-if="verification.type !== VERIFICATION_TYPE_REDEEM">
                    <button
                      v-ripple
                      class="app__form-submit-btn app__button-raised--danger"
                      @click="submit(false)"
                      :disabled="isDisabled"
                    >
                      {{ 'verification-details.btn-reject' | globalize }}
                    </button>
                  </template>
                  <template v-else>
                    <input-field
                      v-model="form.txHash"
                      class="verification-details__hash-input"
                      :error-message="
                        getFieldErrorMessage('form.txHash')"
                      :label="'verification-details.lbl-tx-hash' | globalize"
                      :disabled="formMixin.isDisabled"
                      @blur="touchField('form.txHash')"
                    />
                  </template>
                </permission-wrapper>
              </div>
            </template>
          </div>

          <template v-if="verification.isNotEmptyData">
            <div class="verification-details__section">
              <h3 class="verification-details__subtitle">
                {{ 'verification-details.title-data' | globalize }}
              </h3>

              <details-reader :details="verification.data" />
            </div>
          </template>

          <quickbuy-verification-details-photos
            :quickbuy-verification-details="verification" />

          <template v-if="verification.isNotEmptyReason">
            <div class="verification-details__section">
              <h3 class="verification-details__subtitle">
                {{ 'verification-details.title-reason' | globalize }}
              </h3>
              <details-reader :details="verification.reason" />
            </div>
          </template>
        </div>
      </template>
    </template>
    <template v-else>
      <loader
        message-id="loading"
        position-center />
    </template>
  </div>
</template>

<script>
import Loader from '@/vue/common/Loader'
import ErrorMessage from '@/vue/common/ErrorMessage'
import DetailsReader from '@/vue/common/DetailsReader/DetailsReader'
import FormMixin from '@/vue/mixins/form.mixin'

import QuickbuyVerificationDetailsPhotos from './components/QuickbuyVerificationDetailsPhotos'

import { api } from '@/api'
import { QuickbuyVerificationRecord } from '@/js/records/entities/quickbuy-verification.record'
import { ErrorHandler } from '@/js/helpers/error-handler'
import { errors } from '@/js/errors'
import { required } from '@validators'
import { UserRecord } from '@/js/records/entities/user.record'
import { vueRoutes } from '@/vue-router/routes'
import PermissionWrapper from '@/vue/common/permission-wrapper'

const VERIFICATION_PENDING = 'pending'
const VERIFICATION_TYPE_REDEEM = 'quickbuy_redeem'

export default {
  name: 'quickbuy-verification-details',
  components: {
    Loader,
    ErrorMessage,
    QuickbuyVerificationDetailsPhotos,
    DetailsReader,
    PermissionWrapper,
  },
  mixins: [FormMixin],
  props: {
    id: { type: [String, Number], required: true },
  },
  data () {
    return {
      isLoaded: false,
      isLoadedUserDetails: false,
      isFailed: false,
      isDisabled: false,
      verification: {},
      user: {},
      form: {
        txHash: '',
      },
      errorMessage: 'verification-details.default-error-message',
      VERIFICATION_PENDING,
      VERIFICATION_TYPE_REDEEM,
    }
  },

  validations () {
    return {
      form: {
        txHash: {
          required,
        },
      },
    }
  },

  async created () {
    await this.getQuickbuyVerificationById()
    await this.getUserByRememberMeId()
  },

  methods: {
    async getQuickbuyVerificationById () {
      this.isLoaded = false

      try {
        let { data } = await api.getWithSignature(`/quickbuy/verifications/${this.id}`)
        this.verification = new QuickbuyVerificationRecord(data)
      } catch (error) {
        this.isFailed = true
        switch (error.constructor) {
          case errors.NotFoundError:
            this.errorMessage = 'verification-details.not-found-error-message'
            break
          default:
            this.errorMessage = 'verification-details.default-error-message'
        }
        ErrorHandler.processWithoutFeedback(error)
      }

      this.isLoaded = true
    },

    async getUserByRememberMeId () {
      this.isLoaded = false
      if (!this.verification.rememberMeId) {
        return
      }
      let rememberMeId = encodeURIComponent(this.verification.rememberMeId)
      try {
        let { data } = await api.getWithSignature(`/users/by_remember_me_id/${rememberMeId}`)
        this.user = new UserRecord(data)
      } catch (error) {
        ErrorHandler.processWithoutFeedback(error)
      }

      this.isLoaded = true
    },

    async submit (isApprove) {
      this.isDisabled = true

      const body = JSON.stringify({
        data: {
          type: 'update_quickbuy_verification',
          attributes: {
            status: isApprove ? 'approved' : 'rejected',
            tx_hash: this.form.txHash,
          },
        },
      })

      try {
        await api.patchWithSignature(`/quickbuy/verifications/${this.verification.id}`, body)
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }

      this.isDisabled = false
      this.getQuickbuyVerificationById()
    },

    redirectToUser (id) {
      this.$router.push({
        name: vueRoutes.userDetails.name,
        params: { id },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/vue/forms/app-form';

.verification-details__title {
  margin-bottom: 4rem;
}

.verification-details__subtitle {
  margin-bottom: 2rem;
}

.verification-details__section {
  flex-direction: column;
  margin-top: 3rem;
}

.verification-details__actions {
  justify-content: space-between;
}

.verification-details__log {
  padding: 2rem 0;
  border-bottom: solid 0.1rem;

  &:last-child {
    border: none;
  }
}

.verification-details__actions-wrp {
  display: flex;
  gap: 1rem;
}

.verification-details__img-wrp {
  display: flex;
  gap: 1rem;
  max-width: 15rem;
  width: 100%;
}

.verification-details__dropdown-btn {
  display: flex;
  justify-content: flex-start;
  margin-top: 1rem;
}

.verification-details__dropdown-btn-icon {
  display: block;
  font-size: 2rem;
  height: 2rem;
  width: 2rem;
  line-height: 1.8rem;
  transition: all 0.3s;
}

.verification-details__hash-input {
  max-width: 18rem;
  width: 100%;
  height: 4.2rem;

  @include respond-to($tablet) {
    max-width: 35rem;
  }
  @include respond-to($sidebar-hide-bp) {
    max-width: 65rem;
  }
}

</style>
